import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useThemeStore } from '../../store/themeStore';

// Import SVG assets directly to get resolved URLs
import attentionDarkSvg from '../../assets/brand/svg/03-attention-dark.svg';
import attentionLightSvg from '../../assets/brand/svg/04-attention-light.svg';
import saluteDarkSvg from '../../assets/brand/svg/05-salute-dark.svg';
import saluteLightSvg from '../../assets/brand/svg/06-salute-light.svg';

const SALUTE_DURATION_MS = 3500; // one full salute animation cycle

interface AnimatedLogoProps {
  size?: number;
  className?: string;
}

/**
 * AnimatedLogo keeps the attention SVG always mounted. The salute SVG
 * is only mounted while saluting, with a fresh key each time to restart
 * the CSS animation from the beginning. The attention SVG stays visible
 * underneath as a fallback while the salute loads (prevents flash).
 *
 * Listens for 'agent-salute' custom events to trigger the salute.
 * Automatically picks light/dark variant based on theme.
 */
const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ size = 32, className = '' }) => {
  const [isSaluting, setIsSaluting] = useState(false);
  const saluteKey = useRef(0);

  let resolvedTheme: 'light' | 'dark' = 'light';
  try {
    resolvedTheme = useThemeStore((state) => state.resolvedTheme);
  } catch (_e) {
    resolvedTheme = 'light';
  }

  const isDark = resolvedTheme === 'dark';
  const attentionSrc = isDark ? attentionDarkSvg : attentionLightSvg;
  const saluteSrc = isDark ? saluteDarkSvg : saluteLightSvg;

  const handleSalute = useCallback(() => {
    saluteKey.current += 1;
    setIsSaluting(true);
  }, []);

  useEffect(() => {
    window.addEventListener('agent-salute', handleSalute);
    return () => window.removeEventListener('agent-salute', handleSalute);
  }, [handleSalute]);

  useEffect(() => {
    if (!isSaluting) return;
    const timer = setTimeout(() => setIsSaluting(false), SALUTE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isSaluting]);

  const objStyle: React.CSSProperties = {
    height: `${size}px`,
    width: `${size}px`,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
  };

  return (
    <div className={className} style={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
      {/* Attention is always mounted; hidden during salute to avoid ghosting */}
      <object
        data={attentionSrc}
        type="image/svg+xml"
        aria-label="Agent Commander logo"
        style={{ ...objStyle, visibility: isSaluting ? 'hidden' : 'visible' }}
      />
      {/* Salute mounts on top only while saluting; key forces fresh animation */}
      {isSaluting && (
        <object
          key={saluteKey.current}
          data={saluteSrc}
          type="image/svg+xml"
          aria-hidden="true"
          style={objStyle}
        />
      )}
    </div>
  );
};

export default AnimatedLogo;
